import { Component, useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from '../../../MDC/Icon/Icon';
import PersonSearch from '../PersonSearch';
import axiosInstance from '../../../../../services/axios/axios';
import { Tooltip } from '../../../MDC/Tooltip/Tooltip';
import { checkUsersForUser } from '../../InputElements/ModelAfter/_utilities';
import { appRequestPrimarySearchPropTypes as propTypes } from './_types';
import { reducer, initialState, actionTypes } from './_reducer';

/**
 * Search for primary accounts.  NOTE: To be used only in the context of application requests
 * @see `ModelAfter` component
 * @param {Object} props properties object
 * @returns {Component} Application Request Wrapper of `PersonSearch`
 */
const AppRequestPrimarySearch = props => {
	/**
	 * local reducer for state management
	 */
	const [state, dispatch] = useReducer(reducer, initialState);
	/**
	 * local loading check - necessary for prepopulation
	 */
	const [loading, setLoading] = useState(true);
	/**
	 * is prepopulation being used?
	 */
	const [usePrepop, setUsePrepop] = useState(false);
	/**
	 * what is the prepopulation value
	 */
	const [prepopValue, setPrepopValue] = useState('');

	/**
	 * Redux store selector to get the "form index" (the stringified index of the app)
	 * e.g., "0" for the fist app in the request form
	 */
	const formIndex = useSelector(state =>
		Object.keys(state.apps.allForms).find(
			formIndex => state.apps.allForms[formIndex].id === props.appId
		)
	);

	/**
	 * Requestor's ESO ID
	 */
	const requestorID = useSelector(
		state => state.apps.allForms[formIndex].model.UserID
	);

	const searchUsersFiltered = state.searchUsersFiltered;
	const errorMessage = state.error;
	const isLoading = state.loading;

	const isRequestorInUsers = checkUsersForUser(
		searchUsersFiltered,
		requestorID
	);

	useEffect(() => {
		const extras = JSON.parse(props.extras);
		if (extras) {
			//indicate prepopulation is being used
			setUsePrepop(true);

			//set the prepopulated value
			setPrepopValue(extras.prepop?.row[0]?.Cells['Name']?.Value);

			//invoke the change handler to set the value of the prepopulated user
			let event = {
				target: {
					name: props.name,
					value: extras.prepop?.row[0]?.Cells['F_ESO_ID'].Value,
				},
			};
			props.changeHandler(event, true);
		}
		//regardless of prepopulation, turn loading off
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.extras]);

	/**
	 * call API go query for search results
	 * @param {import('../../../../../store/utility').IMSPacket} packet response payload containing to row selected
	 */
	const search = packet => {
		dispatch({
			type: actionTypes.SEARCH_START,
		});

		//when user clicks 'search', clear the current selection
		let event = {
			target: {
				name: props.name,
				value: '',
			},
		};
		props.changeHandler(event, true);

		axiosInstance
			.post('api/Components/PersonSearch', packet)
			.then(response => {
				switch (response.status) {
					case 200:
						if (response.data) {
							const data = response.data.payload;
							const responseCode = response.data.responseCode;
							const messages = response.data.messages;
							const message =
								Array.isArray(messages) &&
								messages.length > 0 &&
								Object.keys(messages[0]).includes('Value')
									? messages[0].Value
									: 'Error occurred on server';

							switch (responseCode) {
								case 200:
									dispatch({
										type: actionTypes.SEARCH_SUCCESS,
										payload: data,
									});
									dispatch({
										type: actionTypes.CLEAR_MESSAGES,
									});

									messages.forEach(message => {
										dispatch({
											type: actionTypes.ADD_MESSAGE,
											payload: message,
										});
									});
									break;
								default:
									dispatch({
										type: actionTypes.SEARCH_FAILURE,
										payload: message,
									});
									break;
							}
						}
						break;
					default:
						dispatch({
							type: actionTypes.SEARCH_FAILURE,
							payload: 'Unexpected server status.',
						});
						break;
				}
			})
			.catch(error => {
				const message =
					error.response && error.response.data
						? error.response.data.payload
						: error.message;
				dispatch({
					type: actionTypes.SEARCH_FAILURE,
					payload: message ?? 'Unexpected error.',
				});
			});
	};

	const toolTipPosition = document.body.clientWidth > 750 ? 'right' : 'top';
	const labelText = () => {
		return (
			<>
				{props.label ? props.label + ' ' : 'User Search '}
				<Tooltip
					activateOn='hover'
					align={toolTipPosition}
					content='Search for primary accounts.  Inactive accounts will not be displayed in search results.'
				>
					<Icon icon='info' />
				</Tooltip>
			</>
		);
	};
	return loading ? null : (
		<PersonSearch
			dataTable={{
				headers: [
					{
						Value: 'Name',
					},
				],
				label: 'User Results (Please select one)',
				onRowClickedHandler: packet => {
					//  clear search results
					dispatch({ type: actionTypes.CLEAR_RESULTS });

					//  update value, validate
					let event = {
						target: {
							name: props.name,
							value: packet.payload.Cells['F_ESO_ID'].Value,
						},
					};
					props.changeHandler(event, true);
				},
				onTextFieldChangedHandler: e => {
					dispatch({
						type: actionTypes.FILTER_RESULTS,
						payload: e.target.value,
					});
				},
				rows: isRequestorInUsers
					? searchUsersFiltered.filter(
							model =>
								model.Cells['F_ESO_ID'].Value !== requestorID
					  )
					: searchUsersFiltered,
			}}
			displayHeader={{
				Value: 'Name',
			}}
			errorMessage={errorMessage}
			isLoading={isLoading}
			label={labelText()}
			onButtonClickedHandler={packet => {
				if (packet && packet.payload && packet.payload.Input) {
					packet.payload.Input.Extensions = {
						App_ID: props.appId,
					};
				}
				search(packet);
			}}
			onChipRemovedHandler={() => {
				let event = {
					target: {
						name: props.name,
						value: '',
					},
				};
				props.changeHandler(event, true);
			}}
			requestTarget='APP_REQ_PRIMARY'
			value={usePrepop ? prepopValue : props.value}
			warningMessage={
				isRequestorInUsers ? 'Users may not select themselves.' : null
			}
		/>
	);
};

AppRequestPrimarySearch.propTypes = propTypes;

export default AppRequestPrimarySearch;
